// WebCrypto helpers shared by the app and the Worker (both have the same `crypto.subtle`).
//
// Two formats live here:
// - Packed records: [0x01][12-byte IV][AES-256-GCM ciphertext+tag]. Used for every synced
//   record, body and file. Only the app ever holds the keys that open them.
// - Media envelopes: what the Worker writes when an AI job finishes. The Worker can encrypt
//   (it has the user's RSA public key) but never decrypt; only the app's private key opens them.
//   "EMB1" | u32 header length | header JSON | chunks of [IV][ciphertext+tag]

import { MEDIA_CHUNK_BYTES } from './config.ts';

const subtle = crypto.subtle;

export function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(n);
  // getRandomValues fills at most 64 KiB per call.
  for (let i = 0; i < n; i += 65536) {
    crypto.getRandomValues(out.subarray(i, Math.min(n, i + 65536)));
  }
  return out;
}

// ---------- encodings ----------

export function toB64(bytes: Uint8Array): string {
  let bin = '';
  const step = 0x8000;
  for (let i = 0; i < bytes.length; i += step) {
    bin += String.fromCharCode(...bytes.subarray(i, i + step));
  }
  return btoa(bin);
}

export function fromB64(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function toB64Url(bytes: Uint8Array): string {
  return toB64(bytes).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function fromB64Url(s: string): Uint8Array<ArrayBuffer> {
  const pad = s.length % 4 === 0 ? '' : '='.repeat(4 - (s.length % 4));
  return fromB64(s.replace(/-/g, '+').replace(/_/g, '/') + pad);
}

const enc = new TextEncoder();
const dec = new TextDecoder();
export const utf8 = (s: string): Uint8Array<ArrayBuffer> =>
  enc.encode(s) as Uint8Array<ArrayBuffer>;
export const fromUtf8 = (b: Uint8Array): string => dec.decode(b);

export async function sha256(data: Uint8Array | string): Promise<Uint8Array<ArrayBuffer>> {
  const bytes = typeof data === 'string' ? utf8(data) : data;
  return new Uint8Array(await subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>));
}

export async function sha256Hex(data: Uint8Array | string): Promise<string> {
  const h = await sha256(data);
  return Array.from(h, (b) => b.toString(16).padStart(2, '0')).join('');
}

// ---------- key derivation ----------

/** PBKDF2-SHA256 → 32 raw bytes. */
export async function pbkdf2(
  password: string,
  salt: Uint8Array,
  iterations: number,
): Promise<Uint8Array<ArrayBuffer>> {
  const base = await subtle.importKey('raw', utf8(password.normalize('NFKC')), 'PBKDF2', false, [
    'deriveBits',
  ]);
  const bits = await subtle.deriveBits(
    { name: 'PBKDF2', hash: 'SHA-256', salt: salt as Uint8Array<ArrayBuffer>, iterations },
    base,
    256,
  );
  return new Uint8Array(bits);
}

/** HKDF-SHA256 expand of 32 bytes of key material into 32 bytes for one purpose. */
export async function hkdf(ikm: Uint8Array, info: string): Promise<Uint8Array<ArrayBuffer>> {
  const base = await subtle.importKey('raw', ikm as Uint8Array<ArrayBuffer>, 'HKDF', false, [
    'deriveBits',
  ]);
  const bits = await subtle.deriveBits(
    { name: 'HKDF', hash: 'SHA-256', salt: new Uint8Array(32), info: utf8(info) },
    base,
    256,
  );
  return new Uint8Array(bits);
}

export function importAesKey(raw: Uint8Array, extractable = false): Promise<CryptoKey> {
  return subtle.importKey('raw', raw as Uint8Array<ArrayBuffer>, 'AES-GCM', extractable, [
    'encrypt',
    'decrypt',
  ]);
}

export function generateAesKey(): Promise<CryptoKey> {
  // Casts: Workers' types are looser than the DOM's for these calls (same behavior).
  return subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]) as Promise<CryptoKey>;
}

export async function exportRawKey(key: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array((await subtle.exportKey('raw', key)) as ArrayBuffer);
}

// ---------- packed AES-GCM ----------

const PACK_VERSION = 1;

export async function aesEncrypt(
  key: CryptoKey,
  plain: Uint8Array,
): Promise<Uint8Array<ArrayBuffer>> {
  const iv = randomBytes(12);
  const ct = new Uint8Array(
    await subtle.encrypt({ name: 'AES-GCM', iv }, key, plain as Uint8Array<ArrayBuffer>),
  );
  const out = new Uint8Array(1 + 12 + ct.length);
  out[0] = PACK_VERSION;
  out.set(iv, 1);
  out.set(ct, 13);
  return out;
}

export async function aesDecrypt(
  key: CryptoKey,
  packed: Uint8Array,
): Promise<Uint8Array<ArrayBuffer>> {
  if (packed[0] !== PACK_VERSION) throw new Error('Unknown encryption format');
  const iv = packed.subarray(1, 13);
  const ct = packed.subarray(13);
  return new Uint8Array(
    await subtle.decrypt(
      { name: 'AES-GCM', iv: iv as Uint8Array<ArrayBuffer> },
      key,
      ct as Uint8Array<ArrayBuffer>,
    ),
  );
}

export async function encryptJson(
  key: CryptoKey,
  value: unknown,
): Promise<Uint8Array<ArrayBuffer>> {
  return aesEncrypt(key, utf8(JSON.stringify(value)));
}

export async function decryptJson<T = unknown>(key: CryptoKey, packed: Uint8Array): Promise<T> {
  return JSON.parse(fromUtf8(await aesDecrypt(key, packed))) as T;
}

/** Wraps a raw key (e.g. the data key) with a key-encryption key. Returns base64. */
export async function wrapRawKey(raw: Uint8Array, kek: CryptoKey): Promise<string> {
  return toB64(await aesEncrypt(kek, raw));
}

export async function unwrapRawKey(
  wrappedB64: string,
  kek: CryptoKey,
): Promise<Uint8Array<ArrayBuffer>> {
  return aesDecrypt(kek, fromB64(wrappedB64));
}

// ---------- RSA (media envelopes) ----------

const RSA = { name: 'RSA-OAEP', hash: 'SHA-256' } as const;

export async function generateRsaKeyPair(): Promise<CryptoKeyPair> {
  return subtle.generateKey(
    { ...RSA, modulusLength: 3072, publicExponent: new Uint8Array([1, 0, 1]) },
    true,
    ['encrypt', 'decrypt'],
  ) as Promise<CryptoKeyPair>;
}

export async function exportPublicKey(key: CryptoKey): Promise<string> {
  return toB64(new Uint8Array((await subtle.exportKey('spki', key)) as ArrayBuffer));
}

export async function exportPrivateKey(key: CryptoKey): Promise<Uint8Array<ArrayBuffer>> {
  return new Uint8Array((await subtle.exportKey('pkcs8', key)) as ArrayBuffer);
}

export function importPublicKey(spkiB64: string): Promise<CryptoKey> {
  return subtle.importKey('spki', fromB64(spkiB64), RSA, false, ['encrypt']);
}

export function importPrivateKey(pkcs8: Uint8Array): Promise<CryptoKey> {
  return subtle.importKey('pkcs8', pkcs8 as Uint8Array<ArrayBuffer>, RSA, false, ['decrypt']);
}

// ---------- media envelopes ----------

const MAGIC = utf8('EMB1');

export interface MediaHeader {
  v: 1;
  wrappedKey: string;
  chunk: number;
  mime: string;
  bytes: number;
}

/** Worker side: encrypt provider output for the user. Needs only the public key. */
export async function sealMedia(
  publicKey: CryptoKey,
  data: Uint8Array,
  mime: string,
): Promise<Uint8Array<ArrayBuffer>> {
  const fileKey = await generateAesKey();
  const raw = await exportRawKey(fileKey);
  const wrapped = new Uint8Array(await subtle.encrypt(RSA, publicKey, raw));
  const header: MediaHeader = {
    v: 1,
    wrappedKey: toB64(wrapped),
    chunk: MEDIA_CHUNK_BYTES,
    mime,
    bytes: data.length,
  };
  const headerBytes = utf8(JSON.stringify(header));
  const chunks: Uint8Array[] = [];
  let total = 4 + 4 + headerBytes.length;
  for (
    let off = 0;
    off < data.length || (off === 0 && data.length === 0);
    off += MEDIA_CHUNK_BYTES
  ) {
    const iv = randomBytes(12);
    const part = data.subarray(off, Math.min(off + MEDIA_CHUNK_BYTES, data.length));
    const ct = new Uint8Array(
      await subtle.encrypt({ name: 'AES-GCM', iv }, fileKey, part as Uint8Array<ArrayBuffer>),
    );
    chunks.push(iv, ct);
    total += 12 + ct.length;
    if (data.length === 0) break;
  }
  const out = new Uint8Array(total);
  out.set(MAGIC, 0);
  new DataView(out.buffer).setUint32(4, headerBytes.length);
  out.set(headerBytes, 8);
  let pos = 8 + headerBytes.length;
  for (const c of chunks) {
    out.set(c, pos);
    pos += c.length;
  }
  return out;
}

export function readMediaHeader(envelope: Uint8Array): { header: MediaHeader; offset: number } {
  for (let i = 0; i < 4; i++) {
    if (envelope[i] !== MAGIC[i]) throw new Error('Not a media envelope');
  }
  const len = new DataView(envelope.buffer, envelope.byteOffset).getUint32(4);
  const header = JSON.parse(fromUtf8(envelope.subarray(8, 8 + len))) as MediaHeader;
  return { header, offset: 8 + len };
}

/** App side: open an envelope with the user's private key. */
export async function openMedia(
  privateKey: CryptoKey,
  envelope: Uint8Array,
): Promise<{ mime: string; data: Uint8Array<ArrayBuffer> }> {
  const { header, offset } = readMediaHeader(envelope);
  const raw = new Uint8Array(await subtle.decrypt(RSA, privateKey, fromB64(header.wrappedKey)));
  const fileKey = await importAesKey(raw);
  const out = new Uint8Array(header.bytes);
  let pos = offset;
  let written = 0;
  while (written < header.bytes || (header.bytes === 0 && pos < envelope.length)) {
    const plainLen = Math.min(header.chunk, header.bytes - written);
    const iv = envelope.subarray(pos, pos + 12);
    const ct = envelope.subarray(pos + 12, pos + 12 + plainLen + 16);
    const plain = new Uint8Array(
      await subtle.decrypt(
        { name: 'AES-GCM', iv: iv as Uint8Array<ArrayBuffer> },
        fileKey,
        ct as Uint8Array<ArrayBuffer>,
      ),
    );
    out.set(plain, written);
    written += plain.length;
    pos += 12 + plainLen + 16;
    if (header.bytes === 0) break;
  }
  return { mime: header.mime, data: out };
}
